export function finiteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

export function finiteValues(values = []) {
  return values.map(finiteNumber).filter((value) => value !== null);
}

export function mean(values = []) {
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function standardDeviation(values = []) {
  if (!values.length) return null;
  const avg = mean(values);
  const variance = values.reduce((sum, value) => sum + (value - avg) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

export function calculatePercentile(sortedValues = [], percentile) {
  if (!sortedValues.length) return null;
  if (sortedValues.length === 1) return sortedValues[0];
  const position = (percentile / 100) * (sortedValues.length - 1);
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) return sortedValues[lower];
  const weight = position - lower;
  return sortedValues[lower] * (1 - weight) + sortedValues[upper] * weight;
}

export function calculateDistributionStats(rawValues = [], totalCount = rawValues.length) {
  const values = finiteValues(rawValues).sort((left, right) => left - right);
  const missingCount = Math.max(0, totalCount - values.length);
  return {
    totalCount,
    validCount: values.length,
    missingCount,
    missingPercentage: totalCount ? (missingCount / totalCount) * 100 : 0,
    mean: mean(values),
    standardDeviation: standardDeviation(values),
    min: values.length ? values[0] : null,
    p5: calculatePercentile(values, 5),
    p10: calculatePercentile(values, 10),
    p25: calculatePercentile(values, 25),
    median: calculatePercentile(values, 50),
    p75: calculatePercentile(values, 75),
    p90: calculatePercentile(values, 90),
    p95: calculatePercentile(values, 95),
    max: values.length ? values[values.length - 1] : null,
  };
}

export function calculateHistogramBins(rawValues = [], targetBinCount = 12) {
  const values = finiteValues(rawValues);
  if (!values.length) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) {
    return [{ min, max, count: values.length }];
  }
  const binCount = Math.max(4, Math.min(20, Math.ceil(Math.sqrt(values.length)) || targetBinCount));
  const width = (max - min) / binCount;
  const bins = Array.from({ length: binCount }, (_item, index) => ({
    min: min + width * index,
    max: index === binCount - 1 ? max : min + width * (index + 1),
    count: 0,
  }));
  values.forEach((value) => {
    const index = value === max ? binCount - 1 : Math.floor((value - min) / width);
    bins[Math.max(0, Math.min(binCount - 1, index))].count += 1;
  });
  return bins;
}

export function calculatePearsonCorrelation(pairs = []) {
  const clean = pairs
    .map((pair) => ({ x: finiteNumber(pair.x), y: finiteNumber(pair.y) }))
    .filter((pair) => pair.x !== null && pair.y !== null);
  if (clean.length < 2) return null;
  const xMean = mean(clean.map((pair) => pair.x));
  const yMean = mean(clean.map((pair) => pair.y));
  let numerator = 0;
  let xDenominator = 0;
  let yDenominator = 0;
  clean.forEach((pair) => {
    const xDelta = pair.x - xMean;
    const yDelta = pair.y - yMean;
    numerator += xDelta * yDelta;
    xDenominator += xDelta ** 2;
    yDenominator += yDelta ** 2;
  });
  const denominator = Math.sqrt(xDenominator * yDenominator);
  return denominator ? numerator / denominator : null;
}

export function rankValuesWithTies(values = []) {
  const indexed = values.map((value, index) => ({ value, index })).sort((left, right) => left.value - right.value);
  const ranks = Array(values.length);
  let cursor = 0;
  while (cursor < indexed.length) {
    let end = cursor;
    while (end + 1 < indexed.length && indexed[end + 1].value === indexed[cursor].value) end += 1;
    const averageRank = (cursor + 1 + end + 1) / 2;
    for (let i = cursor; i <= end; i += 1) ranks[indexed[i].index] = averageRank;
    cursor = end + 1;
  }
  return ranks;
}

export function calculateSpearmanCorrelation(pairs = []) {
  const clean = pairs
    .map((pair) => ({ x: finiteNumber(pair.x), y: finiteNumber(pair.y) }))
    .filter((pair) => pair.x !== null && pair.y !== null);
  if (clean.length < 2) return null;
  const xRanks = rankValuesWithTies(clean.map((pair) => pair.x));
  const yRanks = rankValuesWithTies(clean.map((pair) => pair.y));
  return calculatePearsonCorrelation(xRanks.map((x, index) => ({ x, y: yRanks[index] })));
}

export function calculateDivergences(pairs = [], limit = 10) {
  const clean = pairs
    .map((pair) => ({ ...pair, x: finiteNumber(pair.x), y: finiteNumber(pair.y) }))
    .filter((pair) => pair.x !== null && pair.y !== null);
  const xValues = clean.map((pair) => pair.x);
  const yValues = clean.map((pair) => pair.y);
  const xMean = mean(xValues);
  const yMean = mean(yValues);
  const xSd = standardDeviation(xValues);
  const ySd = standardDeviation(yValues);
  if (!xSd || !ySd) return [];
  return clean
    .map((pair) => ({
      ...pair,
      standardizedDifference: Math.abs((pair.x - xMean) / xSd - (pair.y - yMean) / ySd),
    }))
    .sort((left, right) => right.standardizedDifference - left.standardizedDifference)
    .slice(0, limit);
}
